import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import OtpInput from 'react-otp-input';
import { useHistory, useLocation } from 'react-router-dom';

import ErrorMessage from '@/components/atoms/ErrorMessage';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Form/Input';
import { Routes } from '@/core/router/routes.types';
import { useAuthSuccess } from '@/hooks/useAuthSuccess';
import logo from '@/pages/Login/logo.svg';
import {
    useLazyMeQuery,
    useVerifyMfaBackupCodeMutation,
    useVerifyMfaMutation,
} from '@/services/innovorder/auth/auth.endpoints';
import type { MfaMethod } from '@/services/innovorder/auth/auth.type';
import { getErrorCode, stringifyError } from '@/utils/errors';

import {
    Actions,
    Card,
    CodeInputWrapper,
    Header,
    Logo,
    MethodCard,
    MethodChooserList,
    OtpBox,
    Page,
    ProductTitle,
    SecondaryLink,
    Subtitle,
    Title,
} from './Mfa.style';
import WebAuthnTab from './MfaVerify/WebAuthnTab';

const TOTP_LENGTH = 6;
const OTP_CONTAINER_STYLE: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
};

type LocationState = { mfaToken?: string; methods?: MfaMethod[] };
type ActiveTab = 'totp' | 'webauthn';

const MfaVerify: React.FunctionComponent = () => {
    const history = useHistory();
    const location = useLocation<LocationState>();
    const mfaToken = location.state?.mfaToken ?? null;
    const methods = location.state?.methods;

    // The API emits methods in priority order — ['webauthn', 'totp'] when both are
    // enrolled. We let the user pick explicitly when both are available; otherwise we
    // route directly to the only supported method.
    const webauthnSupported = useMemo(
        () => typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined',
        [],
    );
    const supportsWebauthn = webauthnSupported && Boolean(methods?.includes('webauthn'));
    const supportsTotp = !methods || methods.includes('totp');
    const hasChoice = supportsWebauthn && supportsTotp;
    const initialChosen: ActiveTab | null = hasChoice ? null : supportsWebauthn ? 'webauthn' : 'totp';
    const [chosen, setChosen] = useState<ActiveTab | null>(initialChosen);

    const [useBackup, setUseBackup] = useState(false);
    const [code, setCode] = useState('');
    const [verify, { isLoading: verifyLoading, error: verifyError, data: verifyData }] = useVerifyMfaMutation();
    const [verifyBackup, { isLoading: backupLoading, error: backupError, data: backupData }] =
        useVerifyMfaBackupCodeMutation();
    const [getMe, { data: meData, error: meError }] = useLazyMeQuery();
    const { handleAuthSuccess, error: authSuccessError } = useAuthSuccess();
    const [bearerToken, setBearerToken] = useState<string | null>(null);

    useEffect(() => {
        if (!mfaToken) history.replace(Routes.Login);
    }, [mfaToken, history]);

    const activeError = verifyError || backupError;
    const activeErrorCode = activeError ? getErrorCode(activeError) : null;

    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            if (!mfaToken || !code) return;
            if (useBackup) {
                verifyBackup({ token: mfaToken, code });
            } else {
                verify({ token: mfaToken, code });
            }
        },
        [mfaToken, code, useBackup, verify, verifyBackup],
    );

    // Auto-submit when the 6th OTP digit lands so the user doesn't have to click Verify.
    // Backup mode keeps manual submit (variable-length code, dash separator).
    const handleOtpChange = useCallback(
        (next: string): void => {
            setCode(next);
            if (next.length === TOTP_LENGTH && mfaToken && !verifyLoading) {
                verify({ token: mfaToken, code: next });
            }
        },
        [mfaToken, verify, verifyLoading],
    );

    useEffect(() => {
        const result = verifyData ?? backupData;
        if (!result?.accessToken) return;
        // 3rd-party redirect flow — the API embeds a validated redirectUri in its response
        // when the login originated from an oauth_client. Hop off to that URL with the
        // bearer appended; the 3rd-party app backend exchanges/stores it from there.
        if (result.redirectUri) {
            const separator = result.redirectUri.includes('?') ? '&' : '?';
            window.location.assign(`${result.redirectUri}${separator}bearer=${result.accessToken}`);
            return;
        }
        setBearerToken(result.accessToken);
        getMe({ token: result.accessToken });
    }, [verifyData, backupData, getMe]);

    useEffect(() => {
        if (meData && bearerToken) {
            handleAuthSuccess(meData, bearerToken);
        }
    }, [meData, bearerToken, handleAuthSuccess]);

    const errorLabel =
        activeErrorCode === 'mfa_verification_failed' ? (
            <FormattedMessage id="mfa.verify.failed" />
        ) : activeErrorCode === 'mfa_token_expired' || activeErrorCode === 'mfa_token_required' ? (
            <FormattedMessage id="mfa.verify.tokenExpired" />
        ) : authSuccessError === 'no_brands_configured' ? (
            <FormattedMessage id="login.noBrandsConfigured" />
        ) : authSuccessError === 'guest_brand_login_failed' ? (
            <FormattedMessage id="login.guestBrandLoginFailed" />
        ) : (
            stringifyError(activeError) || stringifyError(meError)
        );

    const isLoading = verifyLoading || backupLoading;
    const isCodeComplete = useBackup ? code.length > 0 : code.length === TOTP_LENGTH;

    const handleBackToChooser = useCallback(() => {
        setChosen(null);
        setUseBackup(false);
        setCode('');
    }, []);

    return (
        <Page>
            <Header>
                <Logo src={logo} alt="Innovorder" />
                <ProductTitle>Backoffice</ProductTitle>
            </Header>
            <Card onSubmit={chosen === 'totp' ? handleSubmit : (e): void => e.preventDefault()}>
                {chosen === null ? (
                    <>
                        <Title>
                            <FormattedMessage id="mfa.verify.chooser.title" />
                        </Title>
                        <Subtitle>
                            <FormattedMessage id="mfa.verify.chooser.subtitle" />
                        </Subtitle>
                        <MethodChooserList>
                            <MethodCard type="button" onClick={(): void => setChosen('webauthn')}>
                                <strong>
                                    <FormattedMessage id="mfa.verify.tabs.webauthn" />
                                </strong>
                                <span>
                                    <FormattedMessage id="mfa.verify.chooser.webauthnHint" />
                                </span>
                            </MethodCard>
                            <MethodCard type="button" onClick={(): void => setChosen('totp')}>
                                <strong>
                                    <FormattedMessage id="mfa.verify.tabs.totp" />
                                </strong>
                                <span>
                                    <FormattedMessage id="mfa.verify.chooser.totpHint" />
                                </span>
                            </MethodCard>
                        </MethodChooserList>
                    </>
                ) : chosen === 'webauthn' && mfaToken ? (
                    <>
                        <WebAuthnTab mfaToken={mfaToken} />
                        {hasChoice ? (
                            <Actions>
                                <SecondaryLink type="button" onClick={handleBackToChooser}>
                                    <FormattedMessage id="mfa.verify.useDifferentMethod" />
                                </SecondaryLink>
                            </Actions>
                        ) : null}
                    </>
                ) : (
                    <>
                        <Title>
                            <FormattedMessage id={useBackup ? 'mfa.verify.backupTitle' : 'mfa.verify.title'} />
                        </Title>
                        <Subtitle>
                            <FormattedMessage id={useBackup ? 'mfa.verify.backupSubtitle' : 'mfa.verify.subtitle'} />
                        </Subtitle>
                        <CodeInputWrapper>
                            {useBackup ? (
                                <Input
                                    type="text"
                                    value={code}
                                    onChange={(event): unknown => setCode(event.target.value.trim())}
                                    placeholder="XXXX-XXXX"
                                />
                            ) : (
                                <OtpInput
                                    value={code}
                                    onChange={handleOtpChange}
                                    numInputs={TOTP_LENGTH}
                                    shouldAutoFocus
                                    inputType="tel"
                                    skipDefaultStyles
                                    containerStyle={OTP_CONTAINER_STYLE}
                                    renderInput={(props): React.ReactElement => <OtpBox {...props} />}
                                />
                            )}
                        </CodeInputWrapper>

                        {errorLabel ? <ErrorMessage>{errorLabel}</ErrorMessage> : null}

                        <Actions>
                            <Button
                                buttonType="primary"
                                fullWidth
                                thick
                                disabled={!isCodeComplete || isLoading}
                                onClick={handleSubmit}
                            >
                                <FormattedMessage id="mfa.verify.submit" />
                            </Button>
                            <SecondaryLink
                                type="button"
                                onClick={() => {
                                    setUseBackup((v) => !v);
                                    setCode('');
                                }}
                            >
                                <FormattedMessage
                                    id={useBackup ? 'mfa.verify.useTotpInstead' : 'mfa.verify.useBackupInstead'}
                                />
                            </SecondaryLink>
                            {hasChoice ? (
                                <SecondaryLink type="button" onClick={handleBackToChooser}>
                                    <FormattedMessage id="mfa.verify.useDifferentMethod" />
                                </SecondaryLink>
                            ) : null}
                        </Actions>
                    </>
                )}
            </Card>
        </Page>
    );
};

export default MfaVerify;
